import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from './celebrate';

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const isNum = (v) => typeof v === 'number' && isFinite(v);

// Плавно «докручивает» число до target (баланс в шапке, сумма в праздничном экране).
//   duration — длительность прокрутки, мс;
//   delay    — пауза перед прокруткой: число или функция «сколько ещё ждать»,
//              которую хук переспрашивает, пока ждёт (так шапка ждёт монеты);
//   from     — стартовое значение при монтировании (по умолчанию сам target,
//              то есть без анимации при первой отрисовке);
//   disabled — показывать сразу итог.
// Уменьшение значения (вывод денег, синхронизация) показывается сразу, без анимации.
// Возвращает { value, phase }, phase: 'idle' | 'wait' | 'counting' | 'done'.
// 'done' держится bumpMs мс после окончания — для «пружинки» в конце.
export default function useAnimatedNumber(target, options) {
    const opts = options || {};
    const initial = opts.from !== undefined ? opts.from : target;
    const [state, setState] = useState({ value: initial, phase: 'idle' });
    const valueRef = useRef(initial);
    const optsRef = useRef(opts);
    optsRef.current = opts;

    useEffect(() => {
        const o = optsRef.current;
        let raf = 0;
        let delayTimer = 0;
        let doneTimer = 0;
        const from = valueRef.current;
        const to = target;

        if (!isNum(to) || !isNum(from) || to <= from || o.disabled || prefersReducedMotion()) {
            valueRef.current = to;
            setState((s) => (s.value === to && s.phase === 'idle' ? s : { value: to, phase: 'idle' }));
            return undefined;
        }

        const duration = o.duration || 900;
        const begin = () => {
            const startValue = valueRef.current;
            let start = 0;
            const tick = (now) => {
                if (!start) start = now;
                const p = Math.min(1, (now - start) / duration);
                const v = p < 1 ? startValue + (to - startValue) * easeOutCubic(p) : to;
                valueRef.current = v;
                if (p < 1) {
                    setState({ value: v, phase: 'counting' });
                    raf = window.requestAnimationFrame(tick);
                } else {
                    setState({ value: to, phase: 'done' });
                    doneTimer = window.setTimeout(() => {
                        setState((s) => (s.phase === 'done' ? { value: s.value, phase: 'idle' } : s));
                    }, o.bumpMs || 650);
                }
            };
            raf = window.requestAnimationFrame(tick);
        };

        if (typeof o.delay === 'function') {
            // функция может «передумать» (праздник закрыли тапом — монеты прилетят раньше),
            // поэтому, пока ждём, переспрашиваем её не реже чем раз в 150 мс
            const left = o.delay();
            if (left > 0) {
                setState((s) => ({ value: s.value, phase: 'wait' }));
                const poll = () => {
                    const rest = o.delay();
                    if (rest > 0) delayTimer = window.setTimeout(poll, Math.min(rest, 150));
                    else begin();
                };
                delayTimer = window.setTimeout(poll, Math.min(left, 150));
            } else {
                begin();
            }
        } else if (o.delay > 0) {
            setState((s) => ({ value: s.value, phase: 'wait' }));
            delayTimer = window.setTimeout(begin, o.delay);
        } else {
            begin();
        }
        return () => {
            window.cancelAnimationFrame(raf);
            window.clearTimeout(delayTimer);
            window.clearTimeout(doneTimer);
        };
    }, [target]);

    return state;
}
